import { ISettingsRepository } from './ISettingsRepository';
import { ParamUser, ParamActivity, CreateUserPayload, CurrentUserProfile, MyApplicationAccess, ApplicationMember, PersonLookup } from './types';
import { LccRole } from '../../auth/types';
import { APPLICATIONS } from '../../config/applications';

const USERS_KEY = 'lcc.config.users';
const ACTIVITIES_KEY = 'lcc.config.activities';

const SEED_USERS: ParamUser[] = [
  { name: 'CAROLINA SALGADO', role: 'supervisor' as const },
  { name: 'LINA RESTREPO', role: 'operator' as const },
  { name: 'FERNEY ALVAREZ', role: 'operator' as const },
  { name: 'MARISOL RIVAS', role: 'operator' as const },
  { name: 'JHOAN ALEXANDER', role: 'admin' as const },
  { name: 'MICHAEL AVELLANEDA', role: 'operator' as const },
  { name: 'LEIDY SARRAZOLA', role: 'operator' as const },
  { name: 'ADUANADOR', role: 'operator' as const },
  { name: 'MATEO MURIEL', role: 'operator' as const },
].map(({ name, role }) => ({ id: crypto.randomUUID(), fullName: name, email: null, isActive: true, role, allowedModules: null }));

const SEED_ACTIVITIES: ParamActivity[] = [
  'RECIBO', 'CERTIFICACIÓN', 'DESPACHO', 'VALIDACIÓN PICKING', 'INVENTARIO', 'ASEO', 'VALIDACIÓN RETAIL',
].map((name, i) => ({ id: crypto.randomUUID(), name, isActive: true, sortOrder: i }));

function readOrSeed<T>(key: string, seed: T[]): T[] {
  const raw = localStorage.getItem(key);
  if (raw) return JSON.parse(raw);
  localStorage.setItem(key, JSON.stringify(seed));
  return seed;
}
function write<T>(key: string, data: T[]) {
  localStorage.setItem(key, JSON.stringify(data));
}

/** Implementacion "local" (modo desarrollo sin backend): siempre se ve como admin para poder probar todas las pantallas. */
export class LocalSettingsRepository implements ISettingsRepository {
  async getMe(): Promise<CurrentUserProfile> {
    return { id: 'local-dev', fullName: 'DEV LOCAL', role: 'admin', allowedModules: null };
  }

  /** Modo desarrollo: se ve como admin en TODAS las aplicaciones, para poder probar el Selector completo sin backend. */
  async listMyApplications(): Promise<MyApplicationAccess[]> {
    return APPLICATIONS.map((a) => ({ applicationCode: a.code, applicationName: a.name, role: 'admin' as LccRole, allowedModules: null, isActive: true }));
  }

  async listUsers(includeInactive = false): Promise<ParamUser[]> {
    const all = readOrSeed(USERS_KEY, SEED_USERS);
    return includeInactive ? all : all.filter((u) => u.isActive);
  }
  async createUser(payload: CreateUserPayload): Promise<ParamUser> {
    const all = readOrSeed(USERS_KEY, SEED_USERS);
    const user: ParamUser = {
      id: crypto.randomUUID(),
      fullName: payload.fullName.toUpperCase(),
      email: payload.email.trim().toLowerCase(),
      isActive: true,
      role: payload.role ?? 'operator',
      allowedModules: payload.allowedModules ?? null,
    };
    all.push(user);
    write(USERS_KEY, all);
    return user;
  }
  async updateUser(id: string, patch: Partial<ParamUser>): Promise<ParamUser> {
    const all = readOrSeed(USERS_KEY, SEED_USERS);
    const idx = all.findIndex((u) => u.id === id);
    if (idx === -1) throw new Error('Usuario no encontrado');
    all[idx] = { ...all[idx], ...patch };
    write(USERS_KEY, all);
    return all[idx];
  }
  async resetUserPassword(): Promise<void> {
    // Modo desarrollo: no-op (no hay cuentas reales sin Clerk).
  }
  async updateUserProfile(id: string, patch: { fullName?: string; email?: string }): Promise<ParamUser> {
    return this.updateUser(id, patch as Partial<ParamUser>);
  }
  async deleteUser(id: string): Promise<void> {
    write(USERS_KEY, readOrSeed(USERS_KEY, SEED_USERS).filter((u) => u.id !== id));
  }

  /** Modo desarrollo: no distingue aplicaciones - todos los colaboradores locales tienen acceso simulado. */
  async listApplicationMembers(_code: string, includeInactive = false): Promise<ApplicationMember[]> {
    const users = await this.listUsers(includeInactive);
    return users.map((u) => ({ id: u.id, fullName: u.fullName, email: u.email, isActive: u.isActive, role: u.role, allowedModules: u.allowedModules, hasAccess: true }));
  }
  async lookupPersonByEmail(_code: string, email: string): Promise<PersonLookup> {
    const u = (await this.listUsers(true)).find((x) => (x.email ?? '').toLowerCase() === email.trim().toLowerCase());
    return u ? { found: true, person: { id: u.id, fullName: u.fullName, email: u.email, isActive: u.isActive, hasAccessHere: true, role: u.role, applications: [] } } : { found: false };
  }
  async grantApplicationAccess(_code: string, userId: string, role: LccRole, allowedModules: string[] | null = null): Promise<ApplicationMember> {
    const updated = await this.updateUser(userId, { role, allowedModules });
    return { id: updated.id, fullName: updated.fullName, email: updated.email, isActive: updated.isActive, role: updated.role, allowedModules: updated.allowedModules, hasAccess: true };
  }
  async revokeApplicationAccess(): Promise<void> {
    // Modo desarrollo: no-op (no hay separacion real de aplicaciones sin backend).
  }

  async listActivities(includeInactive = false): Promise<ParamActivity[]> {
    const all = readOrSeed(ACTIVITIES_KEY, SEED_ACTIVITIES).sort((a, b) => a.sortOrder - b.sortOrder);
    return includeInactive ? all : all.filter((a) => a.isActive);
  }
  async createActivity(name: string): Promise<ParamActivity> {
    const all = readOrSeed(ACTIVITIES_KEY, SEED_ACTIVITIES);
    const activity: ParamActivity = { id: crypto.randomUUID(), name: name.toUpperCase(), isActive: true, sortOrder: all.length };
    all.push(activity);
    write(ACTIVITIES_KEY, all);
    return activity;
  }
  async updateActivity(id: string, patch: Partial<ParamActivity>): Promise<ParamActivity> {
    const all = readOrSeed(ACTIVITIES_KEY, SEED_ACTIVITIES);
    const idx = all.findIndex((a) => a.id === id);
    if (idx === -1) throw new Error('Actividad no encontrada');
    all[idx] = { ...all[idx], ...patch };
    write(ACTIVITIES_KEY, all);
    return all[idx];
  }
  async deleteActivity(id: string): Promise<void> {
    write(ACTIVITIES_KEY, readOrSeed(ACTIVITIES_KEY, SEED_ACTIVITIES).filter((a) => a.id !== id));
  }
}
